import { useParams, Link } from "react-router-dom";
import { Seo, articleLD, localBusinessLD, breadcrumbLD } from "../lib/seo";
import { POSTS } from "../data/content";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/Shared";
import NotFound from "./NotFound";

export default function BlogPostPage() {
    const { slug } = useParams();
    const post = POSTS.find((p) => p.slug === slug);
    if (!post) return <NotFound />;

    const others = POSTS.filter((p) => p.slug !== slug);

    return (
        <>
            <Seo
                title={`${post.title} | Blog Solino Pro`}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                jsonld={[articleLD(post), localBusinessLD(), breadcrumbLD([
                    { name: "Accueil", path: "/" },
                    { name: "Blog", path: "/blog" },
                    { name: post.title, path: `/blog/${post.slug}` },
                ])]}
            />

            <article data-testid="blog-post">
                <header className="relative pt-40 pb-16" data-testid="blog-post-header">
                    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                        <img src={post.cover} alt="" className="w-full h-full object-cover opacity-25" />
                        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/80 to-ink" />
                    </div>
                    <div className="relative mx-auto max-w-[820px] px-5 sm:px-8">
                        <Reveal>
                            <Link to="/blog" className="text-[11px] uppercase tracking-[0.3em] text-gold hover:text-gold-light" data-testid="blog-post-back">
                                ← Retour au blog
                            </Link>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h1 className="mt-6 text-3xl sm:text-5xl font-light tracking-tighter leading-[1.06] text-ivory">
                                {post.title}
                            </h1>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-smoke">
                                {new Date(post.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} · {post.minRead} min de lecture · Par Solino Pro
                            </p>
                        </Reveal>
                    </div>
                </header>

                <div className="mx-auto max-w-[820px] px-5 sm:px-8 pb-20" data-testid="blog-post-body">
                    {post.sections.map((s, i) => (
                        <Reveal key={s.h} delay={Math.min(i * 0.04, 0.2)}>
                            <section className="mt-10 first:mt-0">
                                <h2 className="text-2xl font-light text-ivory tracking-tight">
                                    <span className="text-gold font-display italic mr-3" aria-hidden="true">§</span>
                                    {s.h}
                                </h2>
                                <div className="mt-5 space-y-4">
                                    {s.b.map((p, j) => (
                                        <p key={j} className="text-smoke leading-relaxed text-[15px]">{p}</p>
                                    ))}
                                </div>
                            </section>
                        </Reveal>
                    ))}

                    <Reveal>
                        <aside className="mt-14 border border-gold/40 bg-gold/5 p-8" data-testid="blog-post-cta">
                            <h2 className="text-xl text-ivory font-medium">Un projet dans le Vexin ou l'Oise ?</h2>
                            <p className="mt-2 text-sm text-smoke leading-relaxed">
                                Solino Pro vient mesurer, conseiller et chiffrer gratuitement —
                                devis détaillé sous 48 h.
                            </p>
                            <Link to="/devis" className="mt-6 inline-flex rounded-full bg-gold text-ink font-bold px-8 py-4 hover:scale-[1.04] transition-transform" data-testid="blog-post-cta-link">
                                Demander mon devis gratuit
                            </Link>
                        </aside>
                    </Reveal>

                    <div className="mt-16">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-6">À lire aussi</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {others.map((o) => (
                                <Link key={o.slug} to={`/blog/${o.slug}`} className="group border border-linew p-6 hover:border-gold/50 transition-colors" data-testid={`blog-post-related-${o.slug}`}>
                                    <span className="block text-ivory group-hover:text-gold transition-colors leading-snug">{o.title}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </article>

            <CtaBand />
        </>
    );
}
